import axios, { AxiosRequestConfig } from 'axios';
import { NO05SearchParams } from '../models/NO05';
import Utils from './Utils';
import CSharpUtils from './CSharpUtils';

const timeout = 30000;

export const loadOpenOrdersSummary = (companyId: string) => {
  const config: AxiosRequestConfig = {
    method: 'get',
    baseURL: `${Utils.URL_ADDRESS}${Utils.WEBAPI_PATH}/orders/summary`,
    timeout,
    headers: { 'content-type': 'application/json', Authorization: 'Bearer ' + sessionStorage.getItem('token') },
    params: { CompanyId: companyId, OpenOrdersOnly: true },
    responseType: 'json',
    responseEncoding: 'utf8',
    withCredentials: false,
  };
  return axios.request(config);
};

// Loads ALL open orders for accurate status card counts.
// No PageSize sent → backend returns all (only ordre_master, no UNION ALL).
// Uses a longer timeout since it may return many rows.
export const loadOpenOrdersStats = (companyId: string) => {
  const config: AxiosRequestConfig = {
    method: 'get',
    baseURL: `${Utils.URL_ADDRESS}${Utils.WEBAPI_PATH}/orders/summary`,
    timeout: 60000,
    headers: { 'content-type': 'application/json', Authorization: 'Bearer ' + sessionStorage.getItem('token') },
    params: { CompanyId: companyId, OpenOrdersOnly: true },
    responseType: 'json',
    responseEncoding: 'utf8',
    withCredentials: false,
  };
  return axios.request(config);
};

export const searchOpenOrders = (searchParams: NO05SearchParams) => {
  const params: any = {
    CompanyId: searchParams.companyId,
    Page: searchParams.page ?? 0,
    PageSize: searchParams.pageSize ?? 30,
    OpenOrdersOnly: true,
  };

  if (searchParams.orderNumber)         params.OrderNumber         = searchParams.orderNumber;
  if (searchParams.customerNumber)      params.CustomerNumber      = searchParams.customerNumber;
  if (searchParams.requisitionNumber)   params.RequisitionNumber   = searchParams.requisitionNumber;
  if (searchParams.productId)           params.ProductId           = searchParams.productId;
  if (searchParams.invoiceCreditNumber) params.InvoiceCreditNumber = searchParams.invoiceCreditNumber;
  if (searchParams.receiver)            params.Receiver            = searchParams.receiver;
  if (searchParams.location)            params.Location            = searchParams.location;
  if (searchParams.address)             params.Address             = searchParams.address;
  if (searchParams.department)          params.Department          = searchParams.department;
  if (searchParams.warehouse)           params.Warehouse           = searchParams.warehouse;
  if (searchParams.fromDate)            params.From                = searchParams.fromDate;
  if (searchParams.toDate)              params.To                  = searchParams.toDate;

  const config: AxiosRequestConfig = {
    method: 'get',
    baseURL: `${Utils.URL_ADDRESS}${Utils.WEBAPI_PATH}/orders/summary`,
    timeout,
    headers: { 'content-type': 'application/json', Authorization: 'Bearer ' + sessionStorage.getItem('token') },
    params,
    responseType: 'json',
    responseEncoding: 'utf8',
    withCredentials: false,
  };
  return axios.request(config);
};

export const getInvoicePDF = (companyId: string, invoiceNumb: string) => {
  const config: AxiosRequestConfig = {
    method: 'get',
    url: `${Utils.URL_ADDRESS}${Utils.WEBAPI_PATH}/invoices/pdf`,
    timeout,
    headers: {
      accept: 'application/json',
      Authorization: `Bearer ${CSharpUtils.Get_Token}`,
    },
    params: { companyId, invoiceNumb },
    responseType: 'blob',
  };
  return axios.request(config);
};

export const fetchOrderDetail = (companyId: number, orderNumber: string) => {
  const config: AxiosRequestConfig = {
    method: 'get',
    baseURL: `${Utils.URL_ADDRESS}${Utils.WEBAPI_PATH}/Order/detail`,
    timeout,
    headers: { 'content-type': 'application/json', Authorization: 'Bearer ' + sessionStorage.getItem('token') },
    params: { orderNumber, companyId },
    responseType: 'json',
    responseEncoding: 'utf8',
    withCredentials: false,
  };
  return axios.request(config);
};

// Batch order-line lookup for the "Med ordrelinjer" export — one request for the whole
// selection instead of one fetchOrderDetail call per order (see GetOrderLinesForExportHandler).
// `orders` carries (recordType, orderNumber) so the backend returns exactly these orders —
// order numbers are recycled across years, and the number alone can match another year's order.
export const fetchOrderLinesForExport = (companyId: number, orders: { orderNumber: string; recordType: string }[]) => {
  const config: AxiosRequestConfig = {
    method: 'post',
    baseURL: `${Utils.URL_ADDRESS}${Utils.WEBAPI_PATH}/orders/lines-for-export`,
    timeout,
    headers: { 'content-type': 'application/json', Authorization: 'Bearer ' + sessionStorage.getItem('token') },
    data: { companyId: String(companyId), orderNumbers: orders.map((o) => o.orderNumber), orders },
    responseType: 'json',
    responseEncoding: 'utf8',
    withCredentials: false,
  };
  return axios.request(config);
};

export const fetchOrderLog = (companyId: string, orderNumber: string) => {
  const config: AxiosRequestConfig = {
    method: 'get',
    baseURL: `${Utils.URL_ADDRESS}${Utils.WEBAPI_PATH}/orders/log`,
    timeout,
    headers: { 'content-type': 'application/json', Authorization: 'Bearer ' + sessionStorage.getItem('token') },
    params: { companyId, orderNumber },
    responseType: 'json',
    responseEncoding: 'utf8',
    withCredentials: false,
  };
  return axios.request(config);
};
